import { getSessionTimerCookie, getSessionDeadlineCookie, getUserIDCookie, isSessionExpired, getTimeUntilExpiration, getSessionTimerExpiration, getSessionTimerValue, getSessionDeadlineExpiration } from '../utils/auth/ClaimsParser';

/**
 * Session timeout service that monitors claims cookie expiration
 * and manages the "keep me logged in" flow. Session timer and deadline are controlled by the backend via cookies.
 */
class SessionTimeoutService {
    constructor() {
        this.timeoutId = null;
        this.warningTimeoutId = null;
        this.onWarningCallback = null;
        this.onExpiredCallback = null;
        this.isMonitoring = false;
        this.lastSessionTimerValue = null; // Track the last SessionTimer cookie value to detect if user is active

        // Configuration
        this.CHECK_INTERVAL_MS = 30 * 1000; // Check every 30 seconds
    }

    /**
     * Start monitoring the claims cookie expiration
     * @param {Function} onWarning - Callback when warning should be shown (2 min before expiry)
     * @param {Function} onExpired - Callback when session has expired
     */
    startMonitoring(onWarning, onExpired) {
        if (this.isMonitoring) {
            this.stopMonitoring();
        }

        this.onWarningCallback = onWarning;
        this.onExpiredCallback = onExpired;
        this.isMonitoring = true;

        this.scheduleNextCheck();
    }

    /**
     * Stop monitoring
     */
    stopMonitoring() {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
        if (this.warningTimeoutId) {
            clearTimeout(this.warningTimeoutId);
            this.warningTimeoutId = null;
        }
        this.isMonitoring = false;
        this.lastSessionTimerValue = null;
    }

    /**
     * Schedule the next check for expiration
     */
    scheduleNextCheck() {
        if (!this.isMonitoring) return;

        const sessionDeadline = getSessionDeadlineCookie();
        const sessionTimer = getSessionTimerCookie();
        const userID = getUserIDCookie();

        if (!sessionDeadline || !userID) {
            return;
        }

        let deadlineExpiration = getSessionDeadlineExpiration();
        let timerExpiration = getSessionTimerExpiration();
        const currentTimerValue = getSessionTimerValue();

        if (!deadlineExpiration) {
            this.triggerExpired();
            return;
        }

        let timerWasUpdated = false;
        if (currentTimerValue) {
            if (this.lastSessionTimerValue === null) {
                this.lastSessionTimerValue = currentTimerValue;
            } else if (currentTimerValue !== this.lastSessionTimerValue) {
                this.lastSessionTimerValue = currentTimerValue;
                timerWasUpdated = true;
            }
        }

        if (timerWasUpdated) {
            timerExpiration = getSessionTimerExpiration();
        }

        const now = Date.now();
        const remainingDeadline = deadlineExpiration - now;
        const remainingTimer = timerExpiration ? timerExpiration - now : null;

        if (remainingDeadline <= 0) {
            this.triggerExpired();
            return;
        }

        this.scheduleWarningAndExpiration(remainingTimer, remainingDeadline);
    }

    /**
     * Schedule warning and expiration based on SessionTimer and SessionDeadline
     * @param {number|null} timeUntilTimerExpirationMs - Time until SessionTimer expires (for warning)
     * @param {number} timeUntilDeadlineExpirationMs - Time until SessionDeadline expires (for logout)
     */
    scheduleWarningAndExpiration(timeUntilTimerExpirationMs, timeUntilDeadlineExpirationMs) {
        if (!this.isMonitoring) return;

        if (!timeUntilDeadlineExpirationMs || timeUntilDeadlineExpirationMs <= 0) {
            this.triggerExpired();
            return;
        }

        // Clear any existing timeouts
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
        if (this.warningTimeoutId) {
            clearTimeout(this.warningTimeoutId);
            this.warningTimeoutId = null;
        }

        // Schedule warning based on SessionTimer expiration
        if (timeUntilTimerExpirationMs && timeUntilTimerExpirationMs > 0) {
            this.warningTimeoutId = setTimeout(() => {
                // Before showing warning, check if SessionTimer has been updated (user is active)
                const currentTimerValue = getSessionTimerValue();
                if (currentTimerValue && currentTimerValue !== this.lastSessionTimerValue) {
                    // Update tracked value and reschedule
                    this.lastSessionTimerValue = currentTimerValue;
                    // Reschedule the check
                    this.scheduleNextCheck();
                } else {
                    this.triggerWarning();
                    // After warning, schedule deadline expiration check
                    this.scheduleExpirationCheck();
                }
            }, timeUntilTimerExpirationMs);
        } else {
            // SessionTimer already expired or not set, check if it was updated first
            const currentTimerValue = getSessionTimerValue();
            if (currentTimerValue && currentTimerValue !== this.lastSessionTimerValue) {
                this.lastSessionTimerValue = currentTimerValue;
                this.scheduleNextCheck();
            } else {
                this.triggerWarning();
                // Schedule deadline expiration check
                this.scheduleExpirationCheck();
            }
        }
    }

    /**
     * Schedule periodic checks for SessionDeadline expiration after warning has been shown
     */
    scheduleExpirationCheck() {
        if (!this.isMonitoring) return;

        const checkExpiration = () => {
            if (!this.isMonitoring) return;

            const userID = getUserIDCookie();

            if (!userID) {
                this.triggerExpired();
                return;
            }

            const deadlineExpiration = getSessionDeadlineExpiration();
            if (!deadlineExpiration) {
                this.triggerExpired();
                return;
            }

            const now = Date.now();
            const remainingDeadline = deadlineExpiration - now;

            if (remainingDeadline <= 0) {
                this.timeoutId = null;
                this.scheduleNextCheck();
                return;
            }

            this.timeoutId = setTimeout(checkExpiration, this.CHECK_INTERVAL_MS);
        };

        // Start checking immediately and then periodically
        this.timeoutId = setTimeout(checkExpiration, this.CHECK_INTERVAL_MS);
    }

    /**
     * Trigger the warning callback
     */
    triggerWarning() {
        if (this.onWarningCallback) {
            this.onWarningCallback();
        }
    }

    /**
     * Trigger the expired callback
     */
    triggerExpired() {
        if (this.onExpiredCallback) {
            this.onExpiredCallback();
        }
    }

    /**
     * Re-read session cookies and reschedule warning/expiration timers.
     * Call this on every successful API response so that when the backend extends
     * the session (new SessionTimer/SessionDeadline cookies), we update our timeline.
     */
    syncFromCookies() {
        if (!this.isMonitoring) return;

        // Clear existing timeouts so we reschedule with fresh cookie values
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
        if (this.warningTimeoutId) {
            clearTimeout(this.warningTimeoutId);
            this.warningTimeoutId = null;
        }

        this.scheduleNextCheck();
    }

    /**
     * Re-read session cookies and reschedule (same as syncFromCookies but also resets
     * lastSessionTimerValue so the new SessionTimer is treated as the baseline).
     * Use this after "Stay Logged In" / refresh so we reschedule from the new cookie values.
     * Delay syncFromCookies so the browser has applied Set-Cookie from the refresh response;
     * reading cookies too soon can see the old (expired) SessionDeadline and trigger logout.
     */
    refreshSession() {
        if (!this.isMonitoring) return;
        this.lastSessionTimerValue = null;
        const delayMs = 1200;
        setTimeout(() => {
            if (!this.isMonitoring) return;
            this.syncFromCookies();
        }, delayMs);
    }

    /**
     * Handle user choosing to stay logged in
     * This should be called after a successful token refresh (with a delay so cookies are set).
     */
    onStayLoggedIn() {
        this.refreshSession();
    }

    /**
     * Handle user choosing to log out or session timing out
     */
    onLogout() {
        this.stopMonitoring();
    }

}

// Export a singleton instance
export const sessionTimeoutService = new SessionTimeoutService();
export default sessionTimeoutService;
